import React, { ReactNode, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Arrow, General } from 'icons/untitledui';

import * as Styled from 'shiftNotes/styles/NotesStack.style';

import StepProgressDots from '../ReflexFeedback/StepProgressDots';
import InstantReflexNavigation from './InstantReflexNavigation';

type Variant = 'missing-info' | 'guidance' | 'answers';
type SubmitVariant = 'submit' | 'approveAll';

interface NoteCardExpandableProps {
  variant: Variant;
  collapsedLabel: string;
  expandedLabel: string;
  currentStep: number;
  totalSteps: number;
  isExpanded?: boolean;
  onToggle?: () => void;
  onPrev: () => void;
  onNext: () => void;
  canGoPrev: boolean;
  canGoNext: boolean;
  isLastStep?: boolean;
  submitVariant?: SubmitVariant;
  headerPreview?: string;
  showPagination?: boolean;
  collapsible?: boolean;
  headerAccessory?: ReactNode;
  stepsCompletedStatus?: boolean[];
  onBackgroundClick?: (event: MouseEvent) => void;
  children: ReactNode;
}

export default function NoteCardExpandable({
  variant,
  collapsedLabel,
  expandedLabel,
  currentStep,
  totalSteps,
  isExpanded = false,
  onToggle,
  onPrev,
  onNext,
  canGoPrev,
  canGoNext,
  isLastStep = false,
  submitVariant = 'submit',
  headerPreview,
  showPagination = true,
  collapsible = true,
  headerAccessory,
  stepsCompletedStatus,
  onBackgroundClick,
  children,
}: NoteCardExpandableProps): JSX.Element {
  const { t } = useTranslation('pageShiftNotes');

  const expanded = collapsible ? isExpanded : true;

  const HeaderIcon =
    variant === 'guidance' ? General.CheckCircle : General.HelpCircle;

  const handleSectionClick = (event: MouseEvent) => {
    if (onBackgroundClick) {
      onBackgroundClick(event);
      return;
    }
    event.stopPropagation();
  };

  const handleToggle = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onToggle?.();
  };

  const handlePrev = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onPrev();
  };

  const handleNext = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onNext();
  };

  const renderNextControl = (): JSX.Element => {
    if (isLastStep && submitVariant === 'approveAll') {
      return (
        <Styled.ApproveAllButton
          $size="small"
          type="button"
          onClick={handleNext}
          disabled={!canGoNext}
          aria-label={t('reflexFeedback.approveAll')}
        >
          {t('reflexFeedback.approveAll')}
          <General.Check />
        </Styled.ApproveAllButton>
      );
    }
    if (isLastStep) {
      return (
        <Styled.SubmitButton
          $size="small"
          type="button"
          onClick={handleNext}
          disabled={!canGoNext}
          aria-label={t('missingInfoCard.nav.submit')}
        >
          <General.Check />
        </Styled.SubmitButton>
      );
    }
    return (
      <Styled.NavButton
        $size="small"
        type="button"
        onClick={handleNext}
        disabled={!canGoNext}
        aria-label={t('missingInfoCard.nav.next')}
      >
        <Arrow.ArrowRight />
      </Styled.NavButton>
    );
  };

  const headerContent = (
    <>
      <Styled.ExpandableHeaderIcon aria-hidden="true">
        <HeaderIcon />
      </Styled.ExpandableHeaderIcon>
      <Styled.ExpandableHeaderLabel>
        {expanded ? expandedLabel : collapsedLabel}
      </Styled.ExpandableHeaderLabel>
      {!expanded && headerPreview && (
        <Styled.ExpandableHeaderPreview>
          {headerPreview}
        </Styled.ExpandableHeaderPreview>
      )}
    </>
  );

  return (
    <Styled.ExpandableSection onClick={handleSectionClick}>
      {collapsible ? (
        <Styled.ExpandableHeader
          $variant={variant}
          type="button"
          onClick={handleToggle}
          aria-expanded={expanded}
        >
          {headerContent}
          <Styled.ExpandableHeaderChevron
            $expanded={expanded}
            aria-hidden="true"
          >
            <Arrow.ChevronDown />
          </Styled.ExpandableHeaderChevron>
        </Styled.ExpandableHeader>
      ) : (
        <Styled.ExpandableHeader as="div" $variant={variant} $static>
          {headerContent}
          {headerAccessory && (
            <Styled.ExpandableHeaderAccessory>
              {headerAccessory}
            </Styled.ExpandableHeaderAccessory>
          )}
        </Styled.ExpandableHeader>
      )}
      {expanded && (
        <Styled.ExpandableBody>
          {children}
          {showPagination && (
            <Styled.Pagination>
              <Styled.NavButton
                $size="small"
                type="button"
                onClick={handlePrev}
                disabled={!canGoPrev}
                aria-label={t('missingInfoCard.nav.previous')}
              >
                <Arrow.ArrowLeft />
              </Styled.NavButton>
              {stepsCompletedStatus ? (
                <StepProgressDots
                  currentStep={currentStep}
                  totalSteps={totalSteps}
                  stepsCompletedStatus={stepsCompletedStatus}
                />
              ) : (
                <InstantReflexNavigation
                  currentStep={currentStep}
                  totalSteps={totalSteps}
                />
              )}
              {renderNextControl()}
            </Styled.Pagination>
          )}
        </Styled.ExpandableBody>
      )}
    </Styled.ExpandableSection>
  );
}
